import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useGetOrganizer } from '../api/organizers';
import { useCreateBooking } from '../api/bookings';
import { Camp } from '../utils/types';
import { Calendar, Clock, Users, DollarSign, ExternalLink, Zap } from 'lucide-react';
import { CampFormatUtils } from '../utils/campFormatUtils';
import { useFeatureFlag } from '../context/FeatureFlagContext';
import { FeatureFlags } from '../utils/featureFlags';
import './OrganizerCampListing.css';

const OrganizerCampListing: React.FC = () => {
  const { organizerId } = useParams<{ organizerId: string }>();
  const { data: organizer, isLoading, error } = useGetOrganizer(organizerId);
  const [selectedDates, setSelectedDates] = useState<Record<string, string>>({});
  const navigate = useNavigate();
  const createBooking = useCreateBooking();
  const instantBookingEnabled = useFeatureFlag(FeatureFlags.INSTANT_BOOKING, false);

  const getCampImage = (camp: Camp): string => {
    return camp.images.find(image => image.type === 'default')?.url || '/images/camp-card-default-image.png';
  };

  const getEffectiveSelectedDate = (camp: Camp): string => {
    // If user selected a date, use that
    if (selectedDates[camp.id]) return selectedDates[camp.id];
    // If only one date, auto-select it
    if (camp.camp_dates.length === 1) return String(camp.camp_dates[0].id);
    return '';
  };

  const isSelectedDateAtCapacity = (camp: Camp): boolean => {
    const dateId = getEffectiveSelectedDate(camp);
    if (!dateId) return false;
    const campDate = camp.camp_dates.find(cd => String(cd.id) === dateId);
    // Note: current_capacity is the number of bookings already made
    return campDate ? campDate.max_capacity - campDate.current_capacity <= 0 : false;
  };

  const handleDateChange = (campId: string, dateId: string) => {
    setSelectedDates(prev => ({ ...prev, [campId]: dateId }));
  };

  const handleBookNow = async (camp: Camp) => {
    const effectiveDate = getEffectiveSelectedDate(camp);
    const useInstantBooking = instantBookingEnabled && camp.organizer?.instant_booking;

    if (!useInstantBooking) {
      if (camp.website) {
        window.open(camp.website, '_blank', 'noopener,noreferrer');
      }
      return;
    }

    try {
      const campDateIds = effectiveDate ? [effectiveDate] : [];
      const booking = await createBooking.mutateAsync({
        camp_id: camp.id,
        camp_date_ids: campDateIds
      });
      navigate(`/checkout?booking_uuid=${booking.uuid}`, { state: { booking } });
    } catch (error) {
      console.error('Failed to create booking:', error);
    }
  };

  const handleMoreDetails = (camp: Camp) => {
    window.open(`/camp/${camp.id}`, '_blank', 'noopener,noreferrer');
  };

  if (isLoading) {
    return (
      <div className="embedded-listing">
        <div className="embedded-loading">
          <div className="loading-spinner"></div>
          <p>Loading camps...</p>
        </div>
      </div>
    );
  }

  if (error || !organizer) {
    return (
      <div className="embedded-listing">
        <div className="embedded-error">
          <p>Unable to load camps at this time.</p>
        </div>
      </div>
    );
  }

  if (organizer.camps.length === 0) {
    return (
      <div className="embedded-listing">
        <div className="embedded-empty">
          <p>No camps available at this time.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="embedded-listing">
      <div className="embedded-camps-list">
        {organizer.camps.map((camp) => {
          const priceParts = CampFormatUtils.getPriceParts(camp);

          return (
            <div key={camp.id} className="embedded-camp-row">
              <div className="embedded-camp-image-container">
                <img
                  src={getCampImage(camp)}
                  alt={camp.name}
                  className="embedded-camp-image"
                />
              </div>

              <div className="embedded-camp-details">
                <h3 className="embedded-camp-name">{camp.name}</h3>

                {camp.description && (
                  <p className="embedded-camp-description">
                    {camp.description.length > 150
                      ? `${camp.description.substring(0, 150).replace(/\s+\S*$/, '')}...`
                      : camp.description}
                  </p>
                )}

                <div className="embedded-camp-info">
                  <div className="embedded-info-item">
                    <Calendar className="icon icon--sm" />
                    {camp.camp_dates.length === 1 ? (
                      <span>
                        {CampFormatUtils.formatDateRange(camp.camp_dates[0].start_date, camp.camp_dates[0].end_date, false)}
                      </span>
                    ) : (
                      <select
                        className="embedded-date-select"
                        value={selectedDates[camp.id] || ''}
                        onChange={(e) => handleDateChange(camp.id, e.target.value)}
                      >
                        <option value="">Select a date</option>
                        {camp.camp_dates
                          .sort((a, b) => new Date(a.start_date).getTime() - new Date(b.start_date).getTime())
                          .map((campDate) => (
                            <option key={campDate.id} value={campDate.id}>
                              {CampFormatUtils.formatDateRange(campDate.start_date, campDate.end_date, false)}
                            </option>
                          ))}
                      </select>
                    )}
                  </div>

                  <div className="embedded-info-item">
                    <Clock className="icon icon--sm" />
                    <span>{CampFormatUtils.formatTimeRange(camp.start_time, camp.end_time)}</span>
                  </div>

                  <div className="embedded-info-item">
                    <Users className="icon icon--sm" />
                    <span>{CampFormatUtils.formatAgeRange(camp.min_age, camp.max_age)}</span>
                  </div>
                </div>
              </div>

              <div className="embedded-camp-actions">
                <div className="embedded-camp-price">
                  <DollarSign className="icon icon--sm" />
                  <span className="price-amount">${priceParts.price}</span>
                  {priceParts.timeframe && (
                    <span className="price-timeframe">/ {priceParts.timeframe}</span>
                  )}
                </div>

                <div className="embedded-buttons">
                  {instantBookingEnabled && camp.organizer?.instant_booking ? (
                    <button
                      className="btn btn--instant-booking btn--sm"
                      onClick={() => handleBookNow(camp)}
                      disabled={!getEffectiveSelectedDate(camp) || isSelectedDateAtCapacity(camp)}
                    >
                      {isSelectedDateAtCapacity(camp) ? (
                        'Camp Full'
                      ) : (
                        <><Zap className="icon icon--xs" /> Book Now</>
                      )}
                    </button>
                  ) : (
                    <button
                      className="btn btn--primary btn--sm"
                      onClick={() => handleBookNow(camp)}
                      disabled={!camp.website}
                    >
                      Book Now
                      <ExternalLink className="icon icon--xs" />
                    </button>
                  )}

                  <button
                    className="btn btn--secondary btn--sm"
                    onClick={() => handleMoreDetails(camp)}
                  >
                    More Details
                    <ExternalLink className="icon icon--xs" />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default OrganizerCampListing;
